import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { dashboardData, chartColors } from '../data/dashboardData';
import { dashboardAPI } from '../services/apiClient';
import { buildDateQueryString } from '../utils/dateParams';

const CompetitorAnalysis = ({ dateRange }) => {
  const { accessories } = dashboardData;

  // State for Competitors (real API data, filtered to exclude Hills)
  const [competitors, setCompetitors] = useState([]);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorsError, setCompetitorsError] = useState(false);

  // State for Complementary Categories (real API data)
  const [complementaryCategories, setComplementaryCategories] = useState([]);
  const [complementaryLoading, setComplementaryLoading] = useState(false);
  const [complementaryError, setComplementaryError] = useState(false);

  // Helper function to build query string with date parameters
  const buildQueryString = () => buildDateQueryString(dateRange);

  // Fetch Competitors data (filtered brand basket share data)
  const fetchCompetitors = async () => {
    setCompetitorsLoading(true);
    setCompetitorsError(false);
    
    try {
      const queryString = buildQueryString();
      const response = await dashboardAPI.getBrandBasketShare(queryString);
      
      const result = response.data;
      // Filter out Hills brands and map to competitor format
      const competitorData = (result.statistics || result || [])
        .filter(item => !item.brand.toLowerCase().includes('hills'))
        .map(item => ({
          brand: item.brand,
          mentions: item.productCount || 0,
          totalSpend: item.totalCost || 0,
          shareOfBasket: item.shareOfBasket || 0
        }));
      
      setCompetitors(competitorData);
    } catch (error) {
      console.error('Error fetching competitors:', error);
      setCompetitorsError(true);
    } finally {
      setCompetitorsLoading(false);
    }
  };

  // Fetch Complementary Categories data
  const fetchComplementaryCategories = async () => {
    setComplementaryLoading(true);
    setComplementaryError(false);
    
    try {
      const queryString = buildQueryString();
      const response = await dashboardAPI.getComplementaryCategories(queryString);
      
      const result = response.data;
      // Map API response to component format
      const categoryData = (result.statistics || result || []).map(item => ({
        category: item.category,
        basketCount: item.basketCount || 0,
        percentOfBaskets: item.percentOfBaskets || 0
      }));
      
      setComplementaryCategories(categoryData);
    } catch (error) {
      console.error('Error fetching complementary categories:', error);
      setComplementaryError(true);
    } finally {
      setComplementaryLoading(false);
    }
  };

  // Data formatters
  const formatCurrency = (value) => `R${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPercentage = (value) => `${value.toFixed(1)}%`;
  const formatNumber = (value) => value.toLocaleString();

  // Fetch data when component mounts or dateRange changes
  useEffect(() => {
    fetchCompetitors();
    fetchComplementaryCategories();
  }, [dateRange]);

  // Competitors Chart Configuration
  const competitorsChartData = competitors.length > 0 
    ? [{ 
        name: 'Product Count', 
        data: competitors.map(comp => comp.mentions || 0)
      }]
    : [{ 
        name: 'Product Count', 
        data: [0]
      }];

  const competitorsChartCategories = competitors.length > 0 
    ? competitors.map(comp => comp.brand || 'Unknown')
    : ['No Data'];

  const competitorsChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: competitorsChartCategories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: [chartColors.secondary],
    title: { 
      text: 'Competitors Detected on Slips',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toLocaleString();
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  // Complementary Categories Chart Configuration
  const complementaryCategoriesChartData = complementaryCategories.length > 0 
    ? [{ 
        name: 'Basket Count', 
        data: complementaryCategories.map(category => category.basketCount || 0)
      }]
    : [{ 
        name: 'Basket Count', 
        data: [0]
      }];

  const complementaryCategoriesChartCategories = complementaryCategories.length > 0 
    ? complementaryCategories.map(category => category.category || 'Unknown')
    : ['No Data'];

  const complementaryCategoriesChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: complementaryCategoriesChartCategories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: [chartColors.info],
    title: { 
      text: "Complementary Product Categories",
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toLocaleString();
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  // Accessories Chart Configuration
  const accessoriesChartData = accessories.length > 0 
    ? [{ 
        name: 'Value', 
        data: accessories.map(acc => acc.value || 0)
      }]
    : [{ 
        name: 'Value', 
        data: [0]
      }];

  const accessoriesChartCategories = accessories.length > 0 
    ? accessories.map(acc => acc.product || 'Unknown')
    : ['No Data'];

  const accessoriesChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: accessoriesChartCategories,
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: [chartColors.light],
    title: { 
      text: "Accessories Purchased Alongside Hill's Dog Food",
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val;
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  return (
    <div className="section-box">
      <div className="section-title">Competitor & Complementary Products</div>
      
      {/* Competitors Detected on Slips */}
      <div className="row">
        <div className="col s12">
          <h5>Competitors Detected on Slips</h5>
          
          {competitorsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="preloader-wrapper active">
                <div className="spinner-layer spinner-blue-only">
                  <div className="circle-clipper left">
                    <div className="circle"></div>
                  </div>
                  <div className="gap-patch">
                    <div className="circle"></div>
                  </div>
                  <div className="circle-clipper right">
                    <div className="circle"></div>
                  </div>
                </div>
              </div>
              <p>Loading competitor data...</p>
            </div>
          ) : competitorsError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>Error loading competitor data</p>
            </div>
          ) : competitors.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>No competitor data available for selected date range</p>
            </div>
          ) : (
            <>
              <div className="chart-container">
                <Chart
                  options={competitorsChartOptions}
                  series={competitorsChartData}
                  type="bar"
                  height={400}
                />
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Competitors Table */}
      {!competitorsLoading && !competitorsError && competitors.length > 0 && (
        <div className="row">
          <div className="col s12">
            <table className="striped">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Product Count</th>
                  <th>Total Spend</th>
                  <th>Share of Basket (%)</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((competitor, index) => (
                  <tr key={index}>
                    <td>{competitor.brand}</td>
                    <td>{formatNumber(competitor.mentions)}</td>
                    <td>{formatCurrency(competitor.totalSpend)}</td>
                    <td>{formatPercentage(competitor.shareOfBasket)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Complementary Products Detected */}
      <div className="row">
        <div className="col s12">
          <h5>Complementary Products Detected</h5>
          
          {complementaryLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="preloader-wrapper active">
                <div className="spinner-layer spinner-blue-only">
                  <div className="circle-clipper left">
                    <div className="circle"></div>
                  </div>
                  <div className="gap-patch">
                    <div className="circle"></div>
                  </div>
                  <div className="circle-clipper right">
                    <div className="circle"></div>
                  </div>
                </div>
              </div>
              <p>Loading complementary categories...</p>
            </div>
          ) : complementaryError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>Error loading complementary categories data</p>
            </div>
          ) : complementaryCategories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>No complementary categories data available for selected date range</p>
            </div>
          ) : (
            <div className="chart-container">
              <Chart
                options={complementaryCategoriesChartOptions}
                series={complementaryCategoriesChartData}
                type="bar"
                height={400}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Complementary Categories Table */}
      {!complementaryLoading && !complementaryError && complementaryCategories.length > 0 && (
        <div className="row">
          <div className="col s12">
            <table className="striped">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Basket Count</th>
                  <th>Percent of Baskets (%)</th>
                </tr>
              </thead>
              <tbody>
                {complementaryCategories.map((category, index) => (
                  <tr key={index}>
                    <td>{category.category}</td>
                    <td>{formatNumber(category.basketCount)}</td>
                    <td>{formatPercentage(category.percentOfBaskets)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Accessories Purchased */}
      <br />
      <div className="row">
        <div className="col s12">
          <h5>Accessories Purchased (Chart)</h5>
          <div className="chart-container">
            <Chart
              options={accessoriesChartOptions}
              series={accessoriesChartData}
              type="bar"
              height={400}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysis; 